/**
 * Integrity Chain Utility
 * Provides SHA-256 hashing and hash chain operations for tamper-evident offline verification bundles.
 * Uses the WebCrypto API (available in all modern browsers and Capacitor WebViews via HTTPS).
 */

/**
 * Compute SHA-256 hash of a string
 * @param {string} data - Input string
 * @returns {Promise<string>} Hex-encoded SHA-256 hash
 */
export async function sha256(data) {
  const encoder = new TextEncoder();
  const buffer = encoder.encode(data);
  const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Compute SHA-256 hash of an ArrayBuffer (for binary data like photos/audio)
 * @param {ArrayBuffer} buffer - Binary data
 * @returns {Promise<string>} Hex-encoded SHA-256 hash
 */
export async function sha256Binary(buffer) {
  const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Hash a JavaScript object deterministically (sorted keys)
 * @param {Object} obj - Object to hash
 * @returns {Promise<string>} Hex-encoded SHA-256 hash
 */
export async function hashObject(obj) {
  const sorted = JSON.stringify(obj, Object.keys(obj).sort());
  return sha256(sorted);
}

/**
 * Create a chained hash: SHA256(currentData + previousHash)
 * This links each piece of evidence to the one before it, making insertion/deletion/reordering detectable.
 * @param {string} currentData - Current item's data string (or its hash)
 * @param {string} previousHash - Previous chain link's hash (empty string for the first item)
 * @returns {Promise<string>} Chained hash
 */
export async function chainHash(currentData, previousHash = '') {
  return sha256(currentData + previousHash);
}

/**
 * Compute the final integrity hash for an entire offline verification session.
 * Excludes the integrity.finalHash field itself to avoid circular reference.
 * @param {Object} session - The complete session object
 * @returns {Promise<string>} Final integrity hash
 */
export async function computeSessionIntegrity(session) {
  // Deep clone and strip the finalHash field before hashing
  const sessionCopy = JSON.parse(JSON.stringify(session));
  if (sessionCopy.integrity) {
    delete sessionCopy.integrity.finalHash;
  }
  return hashObject(sessionCopy);
}

/**
 * Verify the integrity of a session by re-computing the final hash
 * @param {Object} session - The session to verify
 * @returns {Promise<{valid: boolean, expectedHash: string, actualHash: string}>}
 */
export async function verifySessionIntegrity(session) {
  const storedHash = session.integrity?.finalHash;
  if (!storedHash) {
    return { valid: false, expectedHash: storedHash, actualHash: null, reason: 'No finalHash found' };
  }
  const computedHash = await computeSessionIntegrity(session);
  return {
    valid: computedHash === storedHash,
    expectedHash: storedHash,
    actualHash: computedHash
  };
}

/**
 * Verify an entire hash chain (array of items, each with a .hash field)
 * @param {Array<{hash: string}>} items - Ordered array of items with hash fields
 * @param {Array<string>} chainHashes - The stored chain hashes to verify against
 * @returns {Promise<{valid: boolean, brokenAt: number|null}>}
 */
export async function verifyHashChain(items, chainHashes) {
  if (items.length !== chainHashes.length) {
    return { valid: false, brokenAt: 0, reason: 'Length mismatch' };
  }

  let previousHash = '';
  for (let i = 0; i < items.length; i++) {
    const expected = chainHashes[i];
    const computed = await chainHash(items[i].hash, previousHash);
    if (computed !== expected) {
      return { valid: false, brokenAt: i, reason: `Chain broken at index ${i}` };
    }
    previousHash = computed;
  }

  return { valid: true, brokenAt: null };
}

/**
 * Convert a base64 string to an ArrayBuffer for hashing
 * @param {string} base64 - Base64 encoded string
 * @returns {ArrayBuffer}
 */
export function base64ToArrayBuffer(base64) {
  // Strip data URL prefix if present
  const cleaned = base64.includes(',') ? base64.split(',')[1] : base64;
  const binaryString = atob(cleaned);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes.buffer;
}
